// WebAuthn-Client — Passkeys (AUFTRAG E P1).
//
// Bewusst OHNE SimpleWebAuthn-Dependency: die (De-)Serialisierung
// base64url <-> ArrayBuffer ist überschaubar, py_webauthn liefert und
// erwartet Standard-WebAuthn-JSON. Die Challenge lebt in einem signierten
// HttpOnly-Kurz-Cookie, das die Options-Endpoints setzen — der Client
// hält keinerlei Challenge-State.

import { apiFetch } from "./api";

// ----------------------------------------------------------------------
// base64url <-> ArrayBuffer
// ----------------------------------------------------------------------

function b64urlToBuf(v: string): ArrayBuffer {
  const b64 = v.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  const bin = atob(b64 + pad);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function isPasskeySupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "PublicKeyCredential" in window &&
    typeof navigator.credentials?.get === "function"
  );
}

// ----------------------------------------------------------------------
// Login (Discoverable Credential — kein E-Mail-Feld nötig)
// ----------------------------------------------------------------------

interface LoginOptionsJson {
  challenge: string;
  rpId?: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: { id: string; type: "public-key"; transports?: string[] }[];
}

/** Kompletter Passkey-Login: Options holen → Authenticator → Verify.
 *  Erfolg = volle 30-Tage-Session (Cookie), Caller lädt danach neu. */
export async function loginWithPasskey(): Promise<void> {
  const options = await apiFetch<LoginOptionsJson>("/api/v1/auth/passkey/login/options", {
    method: "POST",
  });
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: b64urlToBuf(options.challenge),
    rpId: options.rpId,
    timeout: options.timeout,
    userVerification: options.userVerification,
    allowCredentials: (options.allowCredentials ?? []).map((c) => ({
      type: "public-key",
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Passkey-Dialog abgebrochen");
  const resp = cred.response as AuthenticatorAssertionResponse;

  await apiFetch<void>("/api/v1/auth/passkey/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      credential: {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        clientExtensionResults: cred.getClientExtensionResults(),
        response: {
          clientDataJSON: bufToB64url(resp.clientDataJSON),
          authenticatorData: bufToB64url(resp.authenticatorData),
          signature: bufToB64url(resp.signature),
          userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null,
        },
      },
    }),
  });
}

// ----------------------------------------------------------------------
// Registrierung (nur aus voller Session)
// ----------------------------------------------------------------------

interface RegisterOptionsJson {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { alg: number; type: "public-key" }[];
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  excludeCredentials?: { id: string; type: "public-key"; transports?: string[] }[];
}

/** Registriert einen neuen Passkey für den eingeloggten User. */
export async function registerPasskey(label: string): Promise<void> {
  const options = await apiFetch<RegisterOptionsJson>(
    "/api/v1/auth/passkey/register/options",
    { method: "POST" },
  );
  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: b64urlToBuf(options.challenge),
    rp: options.rp,
    user: {
      id: b64urlToBuf(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout,
    attestation: options.attestation,
    authenticatorSelection: options.authenticatorSelection,
    excludeCredentials: (options.excludeCredentials ?? []).map((c) => ({
      type: "public-key",
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Passkey-Dialog abgebrochen");
  const resp = cred.response as AuthenticatorAttestationResponse;

  await apiFetch<void>("/api/v1/auth/passkey/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      label,
      credential: {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        clientExtensionResults: cred.getClientExtensionResults(),
        response: {
          clientDataJSON: bufToB64url(resp.clientDataJSON),
          attestationObject: bufToB64url(resp.attestationObject),
          transports:
            typeof resp.getTransports === "function" ? resp.getTransports() : undefined,
        },
      },
    }),
  });
}

// ----------------------------------------------------------------------
// Verwaltung
// ----------------------------------------------------------------------

export interface PasskeyItem {
  credential_id: string;
  label: string | null;
  transports: string | null;
  created_at: string;
  last_used_at: string | null;
}

export const fetchPasskeys = () =>
  apiFetch<{ items: PasskeyItem[] }>("/api/v1/auth/passkey/list");
